import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { EvaluatorLayout } from "./EvaluatorLayout";
import { Card, CardContent } from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Input } from "@/project/karuna/components/ui/input"; // @ts-ignore - input is not a valid module
import { Badge } from "@/project/karuna/components/ui/badge"; // @ts-ignore - badge is not a valid module
import { Search, Eye } from "lucide-react";
import { supabase } from "@/project/karuna/integrations/supabase/client"; // @ts-ignore - supabase is not a valid module
import { useToast } from "@/project/karuna/hooks/use-toast"; // @ts-ignore - use-toast is not a valid module
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/project/karuna/components/ui/tabs"; // @ts-ignore - tabs is not a valid module

interface Submission {
  id: string;
  status: string;
  score: number | null;
  submitted_at: string;
  schools: {
    school_name: string;
    kc_no: string;
  };
  events: {
    title: string;
  };
}

const EvaluatorSubmissions = () => {
  const navigate = useNavigate();
  const getLink = useKarunaLink();
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchSubmissions = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("event_submissions")
        .select(
          `
          *,
          schools (school_name, kc_no),
          events (title)
        `
        )
        .order("submitted_at", { ascending: false });

      if (error) {
        throw error;
      }
      setSubmissions(data ?? []);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const filteredSubmissions = submissions.filter(submission => {
    const matchesSearch =
      submission.schools.school_name
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      submission.schools.kc_no
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      submission.events.title.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || submission.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      pending: "secondary",
      approved: "default",
      rejected: "destructive",
    };
    return <Badge variant={variants[status] ?? "secondary"}>{status}</Badge>;
  };

  const stats = {
    all: submissions.length,
    pending: submissions.filter(s => s.status === "pending").length,
    approved: submissions.filter(s => s.status === "approved").length,
    rejected: submissions.filter(s => s.status === "rejected").length,
  };

  return (
    <EvaluatorLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Review Submissions</h1>
          <p className="mt-1 text-muted-foreground">
            Evaluate and score school activity submissions
          </p>
        </div>

        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by school, KC number, or activity..."
              className="pl-10"
            />
          </div>
        </div>

        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList>
            <TabsTrigger value="all">All ({stats.all})</TabsTrigger>
            <TabsTrigger value="pending">Pending ({stats.pending})</TabsTrigger>
            <TabsTrigger value="approved">
              Approved ({stats.approved})
            </TabsTrigger>
            <TabsTrigger value="rejected">
              Rejected ({stats.rejected})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={statusFilter} className="mt-6 space-y-4">
            {loading ? (
              <div className="py-12 text-center">Loading submissions...</div>
            ) : filteredSubmissions.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-muted-foreground">No submissions found</p>
              </Card>
            ) : (
              filteredSubmissions.map(submission => (
                <Card
                  key={submission.id}
                  className="transition-shadow hover:shadow-md"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="mb-2 flex items-center gap-3">
                          <h3 className="text-lg font-semibold">
                            {submission.schools.school_name}
                          </h3>
                          {getStatusBadge(submission.status)}
                        </div>
                        <p className="mb-1 text-sm text-muted-foreground">
                          KC No: {submission.schools.kc_no}
                        </p>
                        <p className="mb-1 text-sm text-muted-foreground">
                          Activity: {submission.events.title}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Submitted:{" "}
                          {new Date(
                            submission.submitted_at
                          ).toLocaleDateString()}
                        </p>
                        {submission.score !== null && (
                          <p className="mt-2 text-sm font-medium">
                            Score: {submission.score}/100
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={() =>
                          navigate(
                            getLink(`/admin/submissions/${submission.id}`)
                          )
                        }
                        size="sm"
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Review
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </EvaluatorLayout>
  );
};

export default EvaluatorSubmissions;
